import React, { useState, useEffect } from 'react';
import { seasonsAPI, handleApiError } from '../services/api';

const HistoricalSeasons = ({ onClose }) => {
  const [archivedSeasons, setArchivedSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [seasonDetails, setSeasonDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadArchivedSeasons();
  }, []);

  const loadArchivedSeasons = async () => {
    try {
      setLoading(true);
      const response = await seasonsAPI.getArchivedSeasons();
      setArchivedSeasons(response.data || []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load archived seasons'));
    } finally {
      setLoading(false);
    }
  };

  const loadSeasonDetails = async (season) => {
    try {
      setLoading(true);
      const response = await seasonsAPI.getArchivedSeasonDetails(season);
      setSeasonDetails(response.data);
      setSelectedSeason(season);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load season details'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatWLRecord = (wins, total) => {
    const losses = total - wins;
    return `${wins}-${losses}`;
  };

  if (loading && archivedSeasons.length === 0) {
    return (
      <div className="modal-overlay">
        <div className="modal-content historical-seasons-modal">
          <div className="loading">Loading archived seasons...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content historical-seasons-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">🏆 Historical Seasons</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
            <button onClick={() => setError('')} className="alert-close">&times;</button>
          </div>
        )}

        <div className="modal-body">
          {archivedSeasons.length === 0 ? (
            <div className="no-archived-seasons">
              <h3>No Archived Seasons Yet</h3>
              <p>Archive a season to start building your history!</p>
            </div>
          ) : (
            <div className="historical-content">
              {/* Season List */}
              <div className="season-list">
                <h3>Seasons</h3>
                {archivedSeasons.map((season) => (
                  <div
                    key={season.id}
                    className={`season-item ${selectedSeason === season.season ? 'active' : ''}`}
                    onClick={() => loadSeasonDetails(season.season)}
                  >
                    <div className="season-year">{season.season}</div>
                    <div className="season-winner">
                      <span className="trophy-small">🏆</span>
                      {season.winner_name}
                    </div>
                    <div className="season-stats-small">
                      {formatWLRecord(season.winner_correct_picks, season.winner_total_picks)}
                      {' '}
                      ({season.winner_percentage.toFixed(1)}%)
                    </div>
                  </div>
                ))}
              </div>

              {/* Season Details */}
              <div className="season-details">
                {!seasonDetails ? (
                  <div className="select-season-prompt">
                    <h3>Select a Season</h3>
                    <p>Click on a season to view detailed standings</p>
                  </div>
                ) : loading ? (
                  <div className="loading">Loading season details...</div>
                ) : (
                  <div className="season-details-content">
                    <div className="season-details-header">
                      <h3>{seasonDetails.season_info.season} Season</h3>
                      <div className="archive-date">
                        Archived: {formatDate(seasonDetails.season_info.archived_at)}
                      </div>
                    </div>

                    <div className="champion-card">
                      <div className="champion-trophy">🏆</div>
                      <div className="champion-info">
                        <div className="champion-label">Season Champion</div>
                        <div className="champion-name">{seasonDetails.season_info.winner_name}</div>
                        <div className="champion-stats">
                          <span className="champion-record">
                            {formatWLRecord(
                              seasonDetails.season_info.winner_correct_picks,
                              seasonDetails.season_info.winner_total_picks
                            )}
                          </span>
                          <span className="champion-percentage">
                            {seasonDetails.season_info.winner_percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="season-weeks">
                        {seasonDetails.season_info.total_weeks} Weeks
                      </div>
                    </div>

                    <div className="standings-section">
                      <h4>Final Standings</h4>
                      {seasonDetails.standings && seasonDetails.standings.length > 0 ? (
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Rank</th>
                              <th>Player</th>
                              <th>W-L</th>
                              <th>Win %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {seasonDetails.standings.map((player, index) => (
                              <tr key={player.id} className={index === 0 ? 'winner-row' : ''}>
                                <td>
                                  <span className="standings-rank">
                                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${player.rank || index + 1}`}
                                  </span>
                                </td>
                                <td>
                                  <strong>{player.name}</strong>
                                </td>
                                <td>
                                  <strong>{formatWLRecord(player.correct_picks, player.total_picks)}</strong>
                                </td>
                                <td>
                                  <span className={`standings-percentage ${player.percentage >= 70 ? 'high' : player.percentage >= 50 ? 'medium' : 'low'}`}>
                                    {player.percentage.toFixed(1)}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p>No standings available for this season.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default HistoricalSeasons;
